import { Component, OnInit, OnDestroy } from '@angular/core';
import { AppleDeviceService } from '../services/apple-device.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-apple-app-banner',
  template: `
    <div *ngIf="showBanner$ | async" class="apple-banner" [class.rtl]="isRtl" [class.mobile]="isMobile">
      <div class="banner-content">
        <button class="banner-close" (click)="dismissLater()" [attr.aria-label]="(getMessage('close') | async) || 'Close'">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
            <path d="M9 7.586L4.707 3.293a1 1 0 00-1.414 1.414L7.586 9l-4.293 4.293a1 1 0 101.414 1.414L9 10.414l4.293 4.293a1 1 0 001.414-1.414L10.414 9l4.293-4.293a1 1 0 00-1.414-1.414L9 7.586z"/>
          </svg>
        </button>
        <div class="banner-main">
          <div class="banner-left">
            <div class="banner-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.08-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
            </div>
            <div class="banner-text">
              <div class="banner-title">{{ (getMessage('title') | async) || 'Download our App' }}</div>
              <div class="banner-subtitle">{{ (getMessage('subtitle') | async) || 'Available on the App Store' }}</div>
            </div>
          </div>
          <a [href]="appStoreUrl" 
             target="_blank" 
             rel="noopener noreferrer"
             class="banner-button"
             (click)="trackClick()">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" class="button-icon">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.08-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span class="button-text">{{ (getMessage('button') | async) || 'Download' }}</span>
          </a>
        </div>
        <div class="banner-footer">
          <label class="checkbox-container">
            <input type="checkbox" [(ngModel)]="dontShowAgain" (change)="onCheckboxChange()">
            <span class="checkbox-label">{{ (getMessage('dontShowAgain') | async) || "Don't show again" }}</span>
          </label>
          <button class="banner-later-btn" (click)="dismissLater()">
            {{ (getMessage('later') | async) || 'Later' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .apple-banner {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10000;
      padding: 0;
      animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
      pointer-events: none;
    }

    .apple-banner.mobile {
      padding: 0 12px 12px 12px;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .banner-content {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 20px 20px 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-bottom: none;
      position: relative;
      overflow: hidden;
      pointer-events: auto;
    }

    .apple-banner.mobile .banner-content {
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.25);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    /* Decorative gradient overlay */
    .banner-content::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
      pointer-events: none;
    }

    .banner-close {
      position: absolute;
      top: 12px;
      right: 12px;
      background: rgba(255, 255, 255, 0.25);
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      min-width: 36px;
      min-height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: white;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      padding: 0;
      z-index: 10;
      -webkit-tap-highlight-color: transparent;
    }

    .banner-close:hover {
      background: rgba(255, 255, 255, 0.35);
      transform: scale(1.1);
    }

    .banner-close:active {
      transform: scale(0.9);
      background: rgba(255, 255, 255, 0.2);
    }

    .apple-banner.rtl .banner-close {
      right: auto;
      left: 12px;
    }

    .banner-main {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px 52px 12px 16px;
    }

    .apple-banner.rtl .banner-main {
      padding: 16px 16px 12px 52px;
    }

    .banner-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 16px 16px 16px;
      gap: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.15);
      margin-top: 4px;
      padding-top: 12px;
    }

    .checkbox-container {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      user-select: none;
      flex: 1;
      min-width: 0;
    }

    .checkbox-container input[type="checkbox"] {
      width: 18px;
      height: 18px;
      min-width: 18px;
      min-height: 18px;
      cursor: pointer;
      accent-color: white;
      border: 2px solid rgba(255, 255, 255, 0.5);
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.1);
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      position: relative;
      transition: all 0.2s ease;
      margin: 0;
      flex-shrink: 0;
    }

    .checkbox-container input[type="checkbox"]:checked {
      background: white;
      border-color: white;
    }

    .checkbox-container input[type="checkbox"]:checked::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #667eea;
      font-size: 14px;
      font-weight: bold;
      line-height: 1;
    }

    .checkbox-container input[type="checkbox"]:hover {
      border-color: rgba(255, 255, 255, 0.8);
      background: rgba(255, 255, 255, 0.15);
    }

    .checkbox-container input[type="checkbox"]:focus {
      outline: 2px solid rgba(255, 255, 255, 0.5);
      outline-offset: 2px;
    }

    .checkbox-label {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.95);
      font-weight: 500;
      line-height: 1.4;
      cursor: pointer;
    }

    .banner-later-btn {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 10px;
      padding: 8px 16px;
      color: white;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      white-space: nowrap;
      min-width: 70px;
      min-height: 36px;
      -webkit-tap-highlight-color: transparent;
    }

    .banner-later-btn:hover {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.4);
      transform: translateY(-1px);
    }

    .banner-later-btn:active {
      transform: translateY(0);
      background: rgba(255, 255, 255, 0.25);
    }

    .banner-left {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;
    }

    .banner-icon {
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      background: rgba(255, 255, 255, 0.25);
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .banner-text {
      flex: 1;
      min-width: 0;
    }

    .banner-title {
      font-size: 16px;
      font-weight: 700;
      color: white;
      margin-bottom: 4px;
      line-height: 1.3;
      letter-spacing: -0.2px;
    }

    .banner-subtitle {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1.4;
      font-weight: 500;
    }

    .banner-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: white;
      color: #667eea;
      padding: 12px 20px;
      border-radius: 14px;
      text-decoration: none;
      font-weight: 700;
      font-size: 15px;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      min-width: 120px;
      min-height: 48px;
      -webkit-tap-highlight-color: transparent;
      flex-shrink: 0;
    }

    .banner-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
      background: #f8f9fa;
    }

    .banner-button:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .button-icon {
      flex-shrink: 0;
      width: 22px;
      height: 22px;
    }

    .button-text {
      font-weight: 700;
      letter-spacing: -0.2px;
    }

    /* RTL Support */
    .apple-banner.rtl .banner-content {
      direction: rtl;
    }

    .apple-banner.rtl .banner-button {
      flex-direction: row-reverse;
    }

    .apple-banner.rtl .banner-footer {
      flex-direction: row-reverse;
    }

    .apple-banner.rtl .checkbox-container {
      flex-direction: row-reverse;
    }

    /* Mobile Optimizations */
    @media (max-width: 768px) {
      .apple-banner {
        padding: 0 12px 12px 12px;
      }

      .banner-content {
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.25);
      }

      .banner-main {
        padding: 14px 48px 10px 14px;
        gap: 10px;
      }

      .apple-banner.rtl .banner-main {
        padding: 14px 14px 10px 48px;
      }

      .banner-footer {
        padding: 0 14px 14px 14px;
        padding-top: 10px;
        gap: 10px;
        flex-wrap: wrap;
      }

      .checkbox-label {
        font-size: 12px;
      }

      .banner-later-btn {
        padding: 7px 14px;
        font-size: 13px;
        min-width: 65px;
        min-height: 34px;
      }

      .banner-icon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
      }

      .banner-left {
        gap: 10px;
      }

      .banner-title {
        font-size: 15px;
        margin-bottom: 3px;
      }

      .banner-subtitle {
        font-size: 12px;
      }

      .banner-button {
        padding: 11px 18px;
        font-size: 14px;
        min-width: 110px;
        min-height: 44px;
        border-radius: 12px;
      }

      .button-icon {
        width: 20px;
        height: 20px;
      }

      .banner-close {
        width: 36px;
        height: 36px;
        min-width: 36px;
        min-height: 36px;
        top: 10px;
        right: 10px;
      }

      .apple-banner.rtl .banner-close {
        left: 10px;
      }
    }

    @media (max-width: 480px) {
      .apple-banner {
        padding: 0 10px 10px 10px;
      }

      .banner-main {
        flex-direction: column;
        align-items: stretch;
        padding: 14px 44px 10px 14px;
        gap: 12px;
      }

      .apple-banner.rtl .banner-main {
        padding: 14px 14px 10px 44px;
      }

      .banner-left {
        width: 100%;
      }

      .banner-button {
        width: 100%;
        min-width: auto;
        justify-content: center;
      }

      .banner-icon {
        width: 40px;
        height: 40px;
      }

      .banner-title {
        font-size: 14px;
      }

      .banner-subtitle {
        font-size: 11px;
      }

      .banner-footer {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 0 14px 14px 14px;
        padding-top: 10px;
      }

      .checkbox-container {
        order: 2;
      }

      .banner-later-btn {
        order: 1;
        width: 100%;
        justify-content: center;
      }
    }

    /* Desktop */
    @media (min-width: 769px) {
      .apple-banner {
        bottom: 20px;
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        max-width: 600px;
        width: calc(100% - 40px);
        padding: 0;
      }

      .banner-content {
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.25);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .banner-main {
        padding: 18px 56px 14px 20px;
      }

      .apple-banner.rtl .banner-main {
        padding: 18px 20px 14px 56px;
      }

      .banner-footer {
        padding: 0 20px 18px 20px;
        padding-top: 14px;
      }

      .banner-close {
        top: 14px;
        right: 14px;
      }

      .apple-banner.rtl .banner-close {
        left: 14px;
      }
    }

    /* Safe area support for iOS */
    @supports (padding: max(0px)) {
      .apple-banner.mobile {
        padding-bottom: max(12px, env(safe-area-inset-bottom));
      }
    }
  `]
})
export class AppleAppBannerComponent implements OnInit, OnDestroy {
  showBanner$: Observable<boolean>;
  appStoreUrl: string;
  isRtl: boolean = false;
  isMobile: boolean = false;
  dontShowAgain: boolean = false;
  private langSubscription?: Subscription;

  constructor(
    private appleDeviceService: AppleDeviceService,
    private translate: TranslateService
  ) {
    this.showBanner$ = this.appleDeviceService.showBanner$;
    this.appStoreUrl = this.appleDeviceService.appStoreUrl;
    this.checkMobile();
  }

  ngOnInit(): void {
    // Check RTL
    const currentLang = localStorage.getItem('curentLang') || 'en';
    this.isRtl = currentLang === 'ar' || currentLang === 'he';
    
    // Watch for language changes
    this.langSubscription = this.translate.onLangChange.subscribe(event => {
      this.isRtl = event.lang === 'ar' || event.lang === 'he';
    });

    // Watch for window resize
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.checkMobile.bind(this));
    }

    // Add/remove body class when banner visibility changes
    this.showBanner$.subscribe(visible => {
      if (typeof document !== 'undefined') {
        if (visible) {
          document.body.classList.add('apple-banner-visible');
        } else {
          document.body.classList.remove('apple-banner-visible');
        }
      }
    });
  }

  private checkMobile(): void {
    if (typeof window !== 'undefined') {
      this.isMobile = window.innerWidth <= 768;
    }
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.checkMobile.bind(this));
    }
  }

  getMessage(key: string): Observable<string> {
    return this.translate.get(`appleBanner.${key}`).pipe(
      map(translation => translation || '')
    );
  }

  dismissLater(): void {
    if (this.dontShowAgain) {
      this.appleDeviceService.dismissBannerPermanently();
    } else {
      this.appleDeviceService.dismissBannerLater();
    }
  }

  onCheckboxChange(): void {
    // Checkbox state is already updated via ngModel
    // This method can be used for additional logic if needed
  }

  trackClick(): void {
    // Optional: Track analytics here
    }
}
